import React from 'react';
import {useSelector} from 'react-redux';
import {Route, Switch} from 'react-router-dom';

import {MY_DEVICES_PAGE_HEADER_TITLE} from '@/consts/localization/localizationKeys';
import ROUTE_PATHS from '@/consts/route/routePaths';
import AboutDevicePage from '@/containers/DevicesPage/components/AboutDevicePage/AboutDevicePage';
import MyDevicesManageDevice from '@/containers/DevicesPage/components/MyDevicesView/components/MyDevicesManageDevice';
import HomePageTopNavigation from '@/containers/HomePageTopNavigation/HomePageTopNavigation';
import useIsMobileLayout from '@/hooks/layout/useIsMobileLayout';
import useDocumentTitle from '@/hooks/useDocumentTitle';
import useLocationPathname from '@/hooks/useLocationPathname';
import useWithLocale from '@/hooks/useWithLocale';
import {HOME_PAGE_NAVIGATION_SOURCE_TYPES} from '@/services/homePageNavigationService';
import {getLocalizedStrings} from '@/services/localization/localizationService';
import productHelpers from '@/services/product/productHelpers';
import {selectCurrentProductInfo} from '@/state/selectors/device';
import HomePageLayout from '@/views/Home/HomePageLayout';
import {WARNING_BANNER_SOURCE_TYPES, WarningBannersTop} from '@/views/WarningBanners';

import DeviceRenamePage from '../DeviceRenamePage/DeviceRenamePage';
import DeviceSettingLayout from '../DeviceSettings/components/DeviceSettingLayout/DeviceSettingLayout';
import {AutoStartSetting} from '../DeviceSettings/views/AutoStart';
import FirmwareInfo from '../DeviceSettings/views/FirmwareInfo/FirmwareInfo';
import FirmwareNotice from '../DeviceSettings/views/FirmwareNotice/FirmwareNotice';
import {FlexPuffSetting} from '../DeviceSettings/views/FlexPuff';
import IlluminationModeSetting from '../DeviceSettings/views/IlluminationMode/IlluminationModeSetting';
import DevicesSettingsSectionWrapper from '../DevicesSettingsSection/DevicesSettingsSectionWrapper';
import MyDevicesImageSection from './components/MyDevicesImageSection/MyDevicesImageSection';
import useFetchUamAssets from './hooks/useFetchUamAssets';

const FULL_PAGE_ROUTES = [];

const MyDevicesView = () => {
    const withLocale = useWithLocale();
    const localizedStrings = getLocalizedStrings();
    const device = useSelector(selectCurrentProductInfo);
    const name = productHelpers.getProductName(device);

    useDocumentTitle(localizedStrings.formatString(localizedStrings[MY_DEVICES_PAGE_HEADER_TITLE], name));

    useFetchUamAssets();

    return (
        <Container device={device} deviceName={name}>
            <Switch>
                <Route path={withLocale(ROUTE_PATHS.DEVICE_RENAME)} component={DeviceRenamePage} />
                <Route path={withLocale(ROUTE_PATHS.ABOUT_DEVICE)} component={AboutDevicePage} />
                <Route path={withLocale(ROUTE_PATHS.AEM_FLEX_BATTERY_SETTING)} component={DeviceSettingLayout} />
                <Route path={withLocale(ROUTE_PATHS.AUTO_START_SETTING)} component={AutoStartSetting} />
                <Route path={withLocale(ROUTE_PATHS.FLEX_PUFF_SETTING)} component={FlexPuffSetting} />
                <Route path={withLocale(ROUTE_PATHS.SMART_GESTURES_SETTING)} component={DeviceSettingLayout} />
                <Route path={withLocale(ROUTE_PATHS.ILLUMINATION_MODE_SETTING)} component={IlluminationModeSetting} />
                <Route path={withLocale(ROUTE_PATHS.VIBRATIONS_SETTING)} component={DeviceSettingLayout} />
                <Route path={withLocale(ROUTE_PATHS.FIRMWARE_INFO)} component={FirmwareInfo} />
                <Route path={withLocale(ROUTE_PATHS.FIRMWARE_NOTICE)} component={FirmwareNotice} />
                <Route component={MyDevicesManageDevice} />
            </Switch>
        </Container>
    );
};

const Container = ({children, device, deviceName}) => {
    const {pathname} = useLocationPathname();
    const isMobile = useIsMobileLayout();
    const localizedStrings = getLocalizedStrings();
    const isFullPage = FULL_PAGE_ROUTES.includes(pathname);

    if (isMobile && isFullPage) {
        return <>{children}</>;
    }

    return (
        <HomePageLayout className='bg-primary-dark-5'>
            <WarningBannersTop source={WARNING_BANNER_SOURCE_TYPES.DEVICES} className='ml:pl-[var(--sidebar-width)]' />
            <HomePageTopNavigation
                title={localizedStrings.formatString(localizedStrings[MY_DEVICES_PAGE_HEADER_TITLE], deviceName)}
                source={HOME_PAGE_NAVIGATION_SOURCE_TYPES.DEVICES}
                profileControlsClassName='bg-romance'
            />
            <div className='pt-safe-offset-sticky-header flex w-full flex-col justify-center md:pt-safe-offset-[calc(var(--sticky-header-height)+32px)] ml:h-[calc(100vh-var(--hw-h))] ml:min-h-full ml:flex-row ml:gap-6 ml:px-8 lg:gap-8 lg:px-16'>
                <MyDevicesImageSection device={device} deviceName={deviceName} />
                <DevicesSettingsSectionWrapper>{children}</DevicesSettingsSectionWrapper>
            </div>
        </HomePageLayout>
    );
};

export default MyDevicesView;
